import { memo, type ReactNode } from 'react';
import type {
  DataMessageType,
  ConfirmationMessageType,
  TerraUIMessage,
  PromptOption,
  BaseDataMessage,
  HITLRequestConfirmationMessage,
  CopilotMode,
} from '@terra/shared-dto-chat-types';
import {
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  type AskUserMessage,
  type HITLDecision,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { AskUserPromptCard } from './AskUserPromptCard';
import { NodeConfirmationPromptCard } from './NodeConfirmationPromptCard';
import { GuardrailConfirmationPromptCard } from './GuardrailConfirmationPromptCard';
import { ToolCallUpdateCard } from './ToolCallUpdateCard';
import {
  CopilotPlanCard,
  type OnOpenPlanInMainContent,
} from './CopilotPlanCard';
import {
  CopilotModeSwitchCard,
  type OnRevertCopilotModeSwitch,
} from './CopilotModeSwitchCard';
import { StopConfirmationPromptCard } from './StopConfirmationPromptCard';
import { HITLRequestConfirmationPromptCard } from './HITL';
import { TodosMessageCard } from './TodosMessageCard';
import { TurnStoppedNotice } from './TurnStoppedNotice';
import type {
  OnOpenEditInMainContent,
  OnCloseEditInMainContent,
  OnOpenHttpInMainContent,
  OnOpenChatObject,
  SkillDraftReference,
} from './hitl-types';
import type { OnOpenNodeArgsInMainContent } from './NodeConfirmationPromptCard';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import { shouldHideExecutableHitlToolInMessageList } from './should-hide-hitl-tool-in-message-list';
import { isLatestToolCallOfName } from '../utils/latest-tool-call';

type DataMessageCardProps = {
  uiMessage: TerraUIMessage;
  dataMessage: ConfirmationMessageType | DataMessageType;
  onDataMessageEvent?: (event: {
    dataMessage: BaseDataMessage;
    selectedOption: PromptOption;
    message?: string;
  }) => void;
  onHITLActionMessageEvent?: (event: {
    dataMessage: HITLRequestConfirmationMessage;
    selectedOption: PromptOption;
    decision: HITLRequestConfirmationMessage['decision'];
    decisions?: HITLDecision[];
  }) => void | Promise<void>;
  onAbortSubagent?: (event: { toolCallId: string }) => void;
  focusedSubagentToolCallId?: string;
  onOpenEditInMainContent?: OnOpenEditInMainContent;
  onCloseEditInMainContent?: OnCloseEditInMainContent;
  onOpenHttpInMainContent?: OnOpenHttpInMainContent;
  onOpenNodeArgsInMainContent?: OnOpenNodeArgsInMainContent;
  onOpenBrowserStreamInMainContent?: (data: { streamingId: string }) => void;
  onOpenPlanInMainContent?: OnOpenPlanInMainContent;
  onBuildPlan?: () => void;
  onRevertCopilotModeSwitch?: OnRevertCopilotModeSwitch;
  onConfirmCopilotModeSwitch?: OnRevertCopilotModeSwitch;
  currentCopilotMode?: CopilotMode;
  onOpenObject?: OnOpenChatObject;
  skillDraftReference?: SkillDraftReference;
  messagePartId?: string;
  selectedValue?: string;
  selectedDecision?: HITLRequestConfirmationMessage['decision'];
  selectedDecisions?: HITLDecision[];
  toolResultsById?: Record<string, ToolCallGenericMessage>;
  onSSESubscribe?: SseSubscribe;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
  hasToolResult?: boolean;
  hideRequestIfShownInHITL?: boolean;
  hitlResolvedToolResultInMessages?: boolean;
  autoCollapseToolCalls?: boolean;
  forceExpandedToolCall?: boolean;
  revealHiddenOutput?: boolean;
  isGroupRunningToolCall?: boolean;
  nestedContent?: ReactNode;
  nestedSummary?: string;
  onEditAskUserAnswer?: (args: {
    toolCallId?: string;
    toolCallPartId?: string;
  }) => void;
};

export const DataMessageCard = memo(function DataMessageCard({
  dataMessage,
  onDataMessageEvent,
  onHITLActionMessageEvent,
  onAbortSubagent,
  focusedSubagentToolCallId,
  onOpenEditInMainContent,
  onCloseEditInMainContent,
  onOpenHttpInMainContent,
  onOpenNodeArgsInMainContent,
  onOpenBrowserStreamInMainContent,
  onOpenPlanInMainContent,
  onBuildPlan,
  onRevertCopilotModeSwitch,
  onConfirmCopilotModeSwitch,
  currentCopilotMode,
  onOpenObject,
  skillDraftReference,
  messagePartId,
  selectedValue,
  selectedDecision,
  selectedDecisions,
  toolResultsById,
  onSSESubscribe,
  isLastMessage,
  isChatLoading,
  hasToolResult,
  hideRequestIfShownInHITL,
  hitlResolvedToolResultInMessages,
  autoCollapseToolCalls,
  forceExpandedToolCall,
  revealHiddenOutput,
  isGroupRunningToolCall,
  nestedContent,
  nestedSummary,
  onEditAskUserAnswer,
}: DataMessageCardProps) {
  const cardType = dataMessage?.type;
  const messageId = dataMessage?.id;

  switch (cardType) {
    case DataMessageTypeEnum.ToolCallUpdate:
    case DataMessageTypeEnum.ToolCallStart:
    case DataMessageTypeEnum.ToolCallComplete:
    case DataMessageTypeEnum.ToolCallError: {
      const hitlDecisionType = selectedDecision?.type;

      const isCoderAbortOnlyPart =
        dataMessage.toolName === 'coder-tool' &&
        dataMessage.status === 'aborted';
      const shouldHideExecutableHitlTool =
        shouldHideExecutableHitlToolInMessageList({
          toolName: dataMessage.toolName,
          status: dataMessage.status,
          toolCallId: dataMessage.toolCallId,
          hideRequestIfShownInHITL,
          hitlResolvedToolResultInMessages,
          hitlDecisionType: selectedDecision?.type,
          toolResultsById,
        });

      if (
        shouldHideExecutableHitlTool ||
        (hideRequestIfShownInHITL && isCoderAbortOnlyPart)
      ) {
        return null;
      }

      return (
        <ToolCallUpdateCard
          key={messageId}
          dataMessage={dataMessage}
          onSSESubscribe={onSSESubscribe}
          hideRequestIfShownInHITL={hideRequestIfShownInHITL}
          hitlDecisionType={hitlDecisionType}
          onOpenHttpInMainContent={onOpenHttpInMainContent}
          onOpenBrowserStreamInMainContent={onOpenBrowserStreamInMainContent}
          autoCollapseWhenComplete={autoCollapseToolCalls}
          forceExpanded={forceExpandedToolCall}
          revealHiddenOutput={revealHiddenOutput}
          isGroupRunning={isGroupRunningToolCall}
          onOpenObject={onOpenObject}
          nestedContent={nestedContent}
          nestedSummary={nestedSummary}
          skillDraftReference={skillDraftReference}
          onEditAskUserAnswer={onEditAskUserAnswer}
          onAbortSubagent={onAbortSubagent}
          focusedSubagentToolCallId={focusedSubagentToolCallId}
          isLatestToolCall={isLatestToolCallOfName({
            toolCallId: dataMessage.toolCallId,
            toolName: dataMessage.toolName,
            toolResultsById,
          })}
        />
      );
    }

    case ConfirmationMessageTypeEnum.NodeConfirmation:
      return (
        <NodeConfirmationPromptCard
          key={messageId}
          dataMessage={dataMessage}
          onDataMessageEvent={onDataMessageEvent}
          selectedValue={selectedValue}
          onOpenNodeArgsInMainContent={onOpenNodeArgsInMainContent}
        />
      );
    case ConfirmationMessageTypeEnum.StopConfirmation:
      return (
        <StopConfirmationPromptCard
          key={messageId}
          dataMessage={dataMessage}
          onDataMessageEvent={onDataMessageEvent}
          selectedValue={selectedValue}
        />
      );
    case ConfirmationMessageTypeEnum.GuardrailConfirmation:
      return (
        <GuardrailConfirmationPromptCard
          key={messageId}
          dataMessage={dataMessage}
          onDataMessageEvent={onDataMessageEvent}
          selectedValue={selectedValue}
        />
      );
    case ConfirmationMessageTypeEnum.HITLRequestConfirmation:
      return (
        <HITLRequestConfirmationPromptCard
          key={messageId}
          dataMessage={dataMessage}
          messagePartId={messagePartId}
          onHITLActionMessageEvent={onHITLActionMessageEvent}
          onOpenEditInMainContent={onOpenEditInMainContent}
          onCloseEditInMainContent={onCloseEditInMainContent}
          onOpenObject={onOpenObject}
          selectedDecision={selectedDecision}
          selectedDecisions={selectedDecisions}
          toolResultsById={toolResultsById}
          isLastMessage={isLastMessage}
          isChatLoading={isChatLoading}
          hasToolResult={hasToolResult}
        />
      );
    case ConfirmationMessageTypeEnum.AskUser:
      return (
        <AskUserPromptCard
          key={messageId}
          dataMessage={dataMessage as AskUserMessage}
          onDataMessageEvent={onDataMessageEvent}
          selectedValue={selectedValue}
        />
      );
    case DataMessageTypeEnum.Todos:
      return (
        <TodosMessageCard
          key={messageId}
          dataMessage={dataMessage}
          isChatLoading={isChatLoading}
        />
      );
    case DataMessageTypeEnum.CopilotPlan:
      return (
        <CopilotPlanCard
          key={messageId}
          dataMessage={dataMessage}
          onOpenPlanInMainContent={onOpenPlanInMainContent}
          onBuildPlan={onBuildPlan}
        />
      );
    case DataMessageTypeEnum.CopilotModeSwitch:
      return (
        <CopilotModeSwitchCard
          key={messageId}
          dataMessage={dataMessage}
          onRevert={onRevertCopilotModeSwitch}
          onConfirm={onConfirmCopilotModeSwitch}
          currentMode={currentCopilotMode}
        />
      );
    case DataMessageTypeEnum.TurnStopped:
      return <TurnStoppedNotice key={messageId} />;
    default:
      return null;
  }
});
